/**
 * Inactivity timeout — signs a logged-in user out after a period of no activity.
 *
 * A payroll app holds SSNs, bank data, and wages; an idle logout limits exposure
 * if someone walks away from a shared or office computer. This is client-side
 * defense-in-depth on top of the server-side token lifetimes (1-hour access,
 * 7-day refresh).
 *
 * Behavior: 30 minutes of no activity → sign out. Two minutes before that, a
 * "Still there?" modal counts down with a "Stay signed in" button, so nobody is
 * silently logged out mid-payroll-run and loses what they were entering. Any
 * real activity (mouse, keyboard, scroll, touch) resets the timer.
 *
 * Only active while authenticated; renders nothing (and arms nothing) otherwise.
 * Mounted once at the app root.
 *
 * @module components/IdleTimeout
 */

import { useState, useEffect, useRef, type ReactElement } from 'react';
import { Modal } from './Modal';
import { useAuth } from '../context/AuthContext';

const IDLE_MS = 30 * 60 * 1000; // sign out after 30 minutes idle
const WARN_MS = 2 * 60 * 1000; // warn for the final 2 minutes
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

/** Set just before an idle sign-out so the Login page can explain why. */
export const IDLE_LOGOUT_FLAG = 'sbp:idleLogout';

export function IdleTimeout(): ReactElement | null {
  const { token, logout } = useAuth();
  /** Seconds remaining in the warning window, or null when not warning. */
  const [warnSeconds, setWarnSeconds] = useState<number | null>(null);
  const lastActivity = useRef(Date.now());

  // Record activity in a ref only — no re-render on every mousemove.
  useEffect((): undefined | (() => void) => {
    if (!token) return undefined;
    const onActivity = (): void => {
      lastActivity.current = Date.now();
    };
    lastActivity.current = Date.now();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
  }, [token]);

  // Once-a-second check drives all UI state from the activity ref.
  useEffect((): undefined | (() => void) => {
    if (!token) {
      setWarnSeconds(null);
      return undefined;
    }
    const id = window.setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_MS) {
        window.clearInterval(id);
        sessionStorage.setItem(IDLE_LOGOUT_FLAG, '1');
        logout();
      } else if (idle >= IDLE_MS - WARN_MS) {
        setWarnSeconds(Math.max(1, Math.ceil((IDLE_MS - idle) / 1000)));
      } else {
        setWarnSeconds((s) => (s === null ? s : null));
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [token, logout]);

  const stay = (): void => {
    lastActivity.current = Date.now();
    setWarnSeconds(null);
  };

  if (!token || warnSeconds === null) return null;

  const mm = Math.floor(warnSeconds / 60);
  const ss = String(warnSeconds % 60).padStart(2, '0');

  return (
    <Modal onClose={stay} labelledBy="idle-timeout-title" cardClassName="max-w-sm p-6">
        <h2 id="idle-timeout-title" className="text-base font-semibold text-gray-900">Still there?</h2>
        <p className="mt-2 text-sm text-gray-600">
          For your security, you'll be signed out in{' '}
          <span className="font-semibold tabular-nums">{mm}:{ss}</span> due to inactivity.
        </p>
        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            onClick={() => { sessionStorage.setItem(IDLE_LOGOUT_FLAG, '1'); logout(); }}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
          >
            Sign out now
          </button>
          <button
            onClick={stay}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            Stay signed in
          </button>
        </div>
    </Modal>
  );
}
